import { isUndefined } from 'lodash-es';
import { type MouseEvent as ReactMouseEvent } from 'react';
import { useIntl } from 'react-intl';

import {
  CrosshairIcon,
  PanHorizontalIcon,
  ZoomToSelectionIcon,
} from '@dynatrace/strato-icons';

import { DownloadDataButton } from './DownloadDataButton.js';
import { ExploreButton } from './ExploreButton.js';
import { PanButton } from './PanButton.js';
import { ResetButton } from './ResetButton.js';
import { ZoomInOutButtons } from './ZoomInOutButtons.js';
import { ZoomModeButton } from './ZoomModeButton.js';
import type { InteractionMode } from '../../../core/components/interactions-overlay/types.js';
import {
  ChartToolbarRenderer,
  type MenuDisplayMode,
} from '../../../core/components/toolbar/ChartToolbarRenderer.js';
import { ChartOverlaysElevation } from '../../../core/constants.js';
import { getA11yToolbarConfig } from '../../../core/utils/zoom.js';
import {
  useInteractionModeState,
  useToolbarState,
} from '../../hooks/store/selectors.js';
import { useChartInteractions } from '../../hooks/useChartInteractions.js';
import { useChartToolbar } from '../../hooks/useChartToolbar.js';
import { useTimeseriesRawData } from '../../hooks/useTimeseriesRawData.js';
import { useTimeseriesSelectionAreaRef } from '../../hooks/useTimeseriesSelectionAreaRef.js';
import { TIMESERIES_MESSAGES } from '../../messages/tooltips-text.js';
import { buildAndDownloadCsv } from '../../utils/download/build-and-download-csv.js';

const defaultToolbarPlacement = 'top-right';

type TimeseriesChartToolbarProps = {
  onMouseEnter?: (event?: ReactMouseEvent<HTMLDivElement, MouseEvent>) => void;
  onMouseLeave?: (event?: ReactMouseEvent<HTMLDivElement, MouseEvent>) => void;
};

const getMenuIcon = (
  interactionMode: InteractionMode,
  isZoomEnabled: boolean | undefined,
) => {
  switch (interactionMode) {
    case 'pan':
      return <PanHorizontalIcon />;
    case 'zoom':
      return isZoomEnabled ? <ZoomToSelectionIcon /> : <CrosshairIcon />;
    default:
      return <CrosshairIcon />;
  }
};

export const TimeseriesChartToolbar = ({
  onMouseEnter,
  onMouseLeave,
}: TimeseriesChartToolbarProps) => {
  const { visible } = useToolbarState();
  const selectionAreaRef = useTimeseriesSelectionAreaRef();

  const config = useChartToolbar();
  const { zoom, zoomX, pan } = useChartInteractions();
  const intl = useIntl();
  const isDownloadEnabled = config?.downloadData?.enabled ?? false;
  const isPanEnabled = pan?.enabled;
  const isZoomEnabled = zoom?.enabled;
  const isZoomXEnabled = zoomX?.enabled;
  const anyInteractionEnabled =
    isZoomEnabled ||
    isZoomXEnabled ||
    isPanEnabled ||
    isDownloadEnabled ||
    config?.intents;

  const mode = useInteractionModeState();
  const menuIcon = getMenuIcon(mode, isZoomEnabled);

  const menuLabel =
    !isPanEnabled && !isZoomEnabled
      ? intl.formatMessage(TIMESERIES_MESSAGES.zoomModeExplore)
      : undefined;

  const rawData = useTimeseriesRawData();

  /** Handles the download button when clicked. */
  const handleDownloadDataToolbar = () => buildAndDownloadCsv(rawData);

  let menuDisplayMode: MenuDisplayMode;

  if (isPanEnabled) {
    if (!isZoomEnabled) {
      menuDisplayMode = 'expanded'; // toolbar is not collapsable
    } else {
      menuDisplayMode = 'always';
    }
  } else {
    if (!isZoomEnabled) {
      menuDisplayMode = 'never';
    } else {
      menuDisplayMode = 'collapsed';
    }
  }

  const onClose = () =>
    selectionAreaRef ? selectionAreaRef.current?.focus() : undefined;

  const showDefaultToolbarDownloadButton =
    isDownloadEnabled && !config?.intents;

  return !isUndefined(config) && anyInteractionEnabled ? (
    <ChartToolbarRenderer
      visible={visible}
      collapsed={config.collapsed}
      draggable={config.draggable}
      placement={config.placement || defaultToolbarPlacement}
      initialA11yState={getA11yToolbarConfig({
        zoomX,
        pan,
        zoom,
        download: config.downloadData,
        intent: !!config.intents,
      })}
      zIndex={ChartOverlaysElevation.toolbar}
      onMouseEnter={onMouseEnter}
      onMouseLeave={onMouseLeave}
      menuIcon={menuIcon}
      menuTriggerLabel={menuLabel}
      menuDisplayMode={menuDisplayMode}
      onClose={onClose}
      isDownloadEnabled={isDownloadEnabled}
      intents={config?.intents}
      handleDownloadDataToolbar={handleDownloadDataToolbar}
      DownloadButton={() => <DownloadDataButton />}
    >
      <>
        <ExploreButton />
        {isZoomXEnabled && <ZoomModeButton />}
        {isPanEnabled && <PanButton />}
      </>
      {isZoomEnabled && <ZoomInOutButtons />}
      <ResetButton />
      {showDefaultToolbarDownloadButton && <DownloadDataButton />}
    </ChartToolbarRenderer>
  ) : null;
};

TimeseriesChartToolbar['displayName'] = 'ToolbarChartToolbar';
